/**
 * AuthContext - Centralized Authentication Management
 * 
 * Single source of truth for authentication state
 * Manages user sessions, login, logout, and authorization checks
 * Session timeout is handled by backend - frontend only handles logout on expiry
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { authService } from '../services';
import { fetchCVODashboard, fetchAuthorityDashboard } from '../store/slices/dashboardSlice';
import authStorage from '../utils/authStorage';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isBarred, setIsBarred] = useState(false);

  // Check authentication status on app load and periodically
  useEffect(() => {
    checkAuth();

    // Set up periodic auth check to ensure user stays logged in
    const authCheckInterval = setInterval(() => {
      if (isAuthenticated) {
        checkAuth();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes

    // Check auth when user returns to the tab
    const handleFocus = () => {
      if (isAuthenticated) {
        checkAuth();
      }
    };

    window.addEventListener('focus', handleFocus);

    return () => {
      clearInterval(authCheckInterval);
      window.removeEventListener('focus', handleFocus);
    };
  }, [isAuthenticated]);

  const checkAuth = () => {
    try {
      const token = authService.getToken();
      const userData = authService.getCurrentUser();

      if (token && userData) {
        // Check if user is barred
        const userBarred = isUserBarred(userData);
        setIsBarred(userBarred);

        if (userBarred) {
          // User is barred - clear authentication but keep user data for display
          setUser(userData);
          setIsAuthenticated(false);
          return;
        }

        setUser(userData);
        setIsAuthenticated(true);

        // Fetch dashboard data based on user role on app initialization/refresh
        const userRole = getUserRoleFromData(userData);
        if (userRole === 'cvo') {
          dispatch(fetchCVODashboard());
        } else if (userRole === 'authority') {
          dispatch(fetchAuthorityDashboard());
        }
      } else {
        // Check for vigilance user data
        const vigilanceUserData = localStorage.getItem('vigilance_user_data');
        if (vigilanceUserData) {
          // IMPORTANT: Check for token even for vigilance users to prevent 
          // "Improper Session Management" (authenticating without a valid token)
          const token = authService.getToken();
          if (!token) {
            setUser(null);
            setIsAuthenticated(false);
            setIsBarred(false);
            return;
          }

          const parsedData = JSON.parse(vigilanceUserData);
          const userBarred = isUserBarred(parsedData);
          setIsBarred(userBarred);

          if (userBarred) {
            // User is barred - keep user data but don't authenticate
            setUser(parsedData);
            setIsAuthenticated(false);
          } else {
            setUser(parsedData);
            setIsAuthenticated(true);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsBarred(false);
        }
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
      setIsBarred(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to extract role from user data
  const getUserRoleFromData = (userData) => {
    if (!userData) return null;

    return userData.role?.name ||
      userData.role ||
      userData.user_role ||
      userData.type ||
      userData.user_type;
  };

  // Helper function to check if user is barred
  const isUserBarred = (userData) => {
    if (!userData) return false;

    // Check various possible barred status fields
    return userData.is_barred === true ||
      userData.barred === true ||
      userData.status === 'barred' ||
      userData.status === 'blocked' ||
      userData.status === 'suspended' ||
      userData.user_status === 'barred' ||
      userData.user_status === 'blocked' ||
      userData.user_status === 'suspended';
  };

  const login = (userData, token) => {
    // Check if user is barred before setting authentication
    const userBarred = isUserBarred(userData);
    setIsBarred(userBarred);

    if (userBarred) {
      // User is barred - keep user data but don't authenticate
      setUser(userData);
      setIsAuthenticated(false);
    } else {
      setUser(userData);
      setIsAuthenticated(true);
    }
  };

  const logout = async () => {
    try {
      // Get user role before clearing state
      const userRole = getUserRole();

      // Call logout with user role for role-specific endpoint
      if (userRole === 'cvo' || userRole === 'authority' || userRole === 'citizen') {
        await authService.logout(userRole);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear all user data from service (handles localStorage and persist keys)
      authService.clearAuthData();

      // Also clear specifically named keys in context for redundancy
      localStorage.removeItem('vigilance_user_data');
      localStorage.removeItem('user_data');
      authStorage.removeItem('auth_token');
      localStorage.removeItem('persist:vigilance-root');

      // Reset auth state in context
      setUser(null);
      setIsAuthenticated(false);
      setIsBarred(false);

      // Force a reload or complex cleanup if necessary, 
      // but at minimum ensure dispatch(clearAuth) and dispatch(clearDashboard) are called
      // if these were reliably available via imports, we'd call them here.
      // Given the slice structure, we'll try to use actions if we can.
    }
  };

  const getUserRole = () => {
    if (!user) return null;

    return getUserRoleFromData(user);
  };

  const isAuthorized = (requiredRole = null) => {
    if (!isAuthenticated) return false;
    if (!requiredRole) return true;

    const userRole = getUserRole();
    return userRole === requiredRole;
  };

  const getDashboardPath = () => {
    const role = getUserRole();

    console.log('Getting dashboard path for role:', role);

    if (role === "cvo") {
      return '/cvo-dashboard';
    } else if (role === "authority") {
      return '/authority-dashboard';
    } else if (role === "citizen" || role === "user") {
      return '/vigilance-dashboard';
    }

    // Fallback to home if role is not recognized
    console.warn('Unknown role, redirecting to home:', role);
    return '/';
  };

  const getBarredUserRedirectPath = () => {
    const role = getUserRole();

    console.log('Getting barred user redirect path for role:', role);

    // Redirect barred users to their respective login pages
    if (role === "cvo") {
      return '/cvo-login';
    } else if (role === "authority") {
      return '/authority-login';
    } else if (role === "citizen" || role === "user") {
      return '/vigilance-login';
    }

    // Fallback to home if role is not recognized
    console.warn('Unknown role for barred user, redirecting to home:', role);
    return '/';
  };

  const value = {
    user,
    isAuthenticated,
    isLoading,
    isBarred,
    login,
    logout,
    checkAuth,
    getUserRole,
    isAuthorized,
    getDashboardPath,
    getBarredUserRedirectPath,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;

